/**
 * RouteTracker Component
 * Automatically tracks route changes and page views
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import analyticsService from '../services/analyticsService';
import { trackPerformance } from '../utils/performanceMonitoring';

const RouteTracker = () => {
  const location = useLocation();
  const previousPath = useRef(null);
  const routeStartTime = useRef(performance.now());
  const pageLoadTime = useRef(null);

  useEffect(() => {
    const currentPath = location.pathname;
    const currentTime = performance.now();
    
    // Debug logging
    const debugMode = process.env.REACT_APP_DEBUG === 'true';
    if (debugMode) {
      console.log(`🛣️ RouteTracker: Route changed to ${currentPath}`);
    }

    // Track route transition time
    if (previousPath.current) {
      const transitionTime = currentTime - routeStartTime.current;
      trackPerformance('route_transition', transitionTime, {
        from: previousPath.current,
        to: currentPath,
      });
    }

    // Track page view
    if (debugMode) {
      console.log(`🛣️ RouteTracker: Calling trackPageView for ${currentPath}`);
    }
    analyticsService.trackPageView(currentPath, {
      search: location.search,
      hash: location.hash,
      previousPath: previousPath.current,
    });

    // Measure page load time (time to interactive)
    if (!pageLoadTime.current) {
      const loadTime = currentTime;
      pageLoadTime.current = loadTime;
      trackPerformance('initial_page_load', loadTime, {
        path: currentPath,
      });
    }

    // Update refs for next navigation
    previousPath.current = currentPath;
    routeStartTime.current = currentTime;

    // Track route-specific metrics
    trackRouteMetrics(currentPath);
  }, [location]);

  return null; // This is a tracking component, no UI
};

/**
 * Track route-specific metrics (Customer module only)
 */
const trackRouteMetrics = (path) => {
  // Only track customer-related routes
  if (path === '/') {
    analyticsService.track('homepage_visit');
  } else if (path.startsWith('/products/')) {
    analyticsService.track('product_page_visit', {
      productId: path.split('/')[2],
    });
  } else if (path === '/cart') {
    analyticsService.track('cart_page_visit');
  } else if (path === '/orders') {
    analyticsService.track('orders_page_visit');
  } else if (path === '/profile') {
    analyticsService.track('profile_page_visit');
  } else if (path === '/address') {
    analyticsService.track('address_page_visit');
  } else if (path === '/login') {
    analyticsService.track('login_page_visit');
  } else if (path === '/signup') {
    analyticsService.track('signup_page_visit');
  }
  // Note: seller, admin, delivery, logistics routes are ignored (no tracking)
};

export default RouteTracker;
